import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    X, Loader2, ShieldCheck, ShieldAlert, CheckCircle2, AlertTriangle, PencilLine, Save, ArrowRight, Wallet,
} from 'lucide-react';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { podeEditarCadastro } from '../../src/lib/permissaoCadastro';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { supabase } from '../../src/lib/supabase';
import DocumentosCancelamento from './DocumentosCancelamento';
import {
    carregarContextoValidacao, validarCancelamento, corrigirCadastroUc, registrarValidacao,
} from '../../src/lib/validacaoCancelamento';
import type {
    ContextoValidacao, ItemValidacao, SeveridadeValidacao, CampoUcEditavel,
} from '../../src/lib/validacaoCancelamento';
import type { SolicitacaoCancelamentoDetalhada } from '../../src/lib/cancelamentos';
import { montarAnaliseFinanceira, plural } from '../../src/lib/analiseFinanceiraCancelamento';
import type { AnaliseFinanceira } from '../../src/lib/analiseFinanceiraCancelamento';
import { hojeEmBrasilia } from '../../src/lib/prazoRetornoCancelamento';

interface ValidarCancelamentoModalProps {
    isOpen: boolean;
    solicitacao: SolicitacaoCancelamentoDetalhada | null;
    onClose: () => void;
    /** Validação carimbada: o pai abre o CancelUCModal para emitir o distrato. */
    onValidado: () => void;
}

const fmtBRLv = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtKwhV = (v: number) => `${Math.round(v).toLocaleString('pt-BR')} kWh`;
const fmtDataBr = (iso: string) => iso.split('-').reverse().join('/');

/**
 * Uma linha do bloco financeiro (#5829). Sem valor calculado mostra o MOTIVO —
 * nunca "R$ 0,00", que o atendente passaria ao cliente como isenção.
 */
const LinhaFinanceira: React.FC<{ rotulo: string; valor?: React.ReactNode; detalhe?: string | null; motivo?: string | null }> =
    ({ rotulo, valor, detalhe, motivo }) => (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
        <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 shrink-0">{rotulo}</span>
        <span className="text-right min-w-0">
            {motivo
                ? <span className="text-xs text-gray-400 dark:text-slate-500 italic">não calculado — {motivo}</span>
                : <>
                    <span className="text-sm font-bold text-gray-800 dark:text-slate-100">{valor}</span>
                    {detalhe ? <span className="block text-left text-[11px] text-gray-400 dark:text-slate-500">{detalhe}</span> : null}
                </>}
        </span>
    </div>
);

const inputCls = "w-full border rounded-xl px-3 py-2 text-sm outline-none bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary";
const labelCls = "block text-[11px] font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider";

/** Campos do cadastro da UC que alimentam o distrato — a tela-resumo é o próprio formulário. */
const CAMPOS: Array<{ campo: CampoUcEditavel; rotulo: string; largura: string; placeholder?: string }> = [
    { campo: 'nome_cliente', rotulo: 'Nome do cliente', largura: 'md:col-span-4' },
    { campo: 'cpf_cnpj_cliente', rotulo: 'CPF / CNPJ', largura: 'md:col-span-2' },
    { campo: 'email_cliente', rotulo: 'E-mail', largura: 'md:col-span-3' },
    { campo: 'whatsapp_cliente', rotulo: 'WhatsApp', largura: 'md:col-span-3', placeholder: '(62) 99999-9999' },
    { campo: 'cep', rotulo: 'CEP', largura: 'md:col-span-2' },
    { campo: 'logradouro', rotulo: 'Rua', largura: 'md:col-span-3' },
    { campo: 'numero', rotulo: 'Nº', largura: 'md:col-span-1' },
    { campo: 'bairro', rotulo: 'Bairro', largura: 'md:col-span-2' },
    { campo: 'cidade', rotulo: 'Cidade', largura: 'md:col-span-3' },
    { campo: 'estado', rotulo: 'UF', largura: 'md:col-span-1' },
];

const SEVERIDADE_META: Record<SeveridadeValidacao, { icone: React.ElementType; cls: string; caixa: string; rotulo: string }> = {
    ok: {
        icone: CheckCircle2, rotulo: 'Tudo certo',
        cls: 'text-emerald-600 dark:text-emerald-400',
        caixa: 'border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40',
    },
    atencao: {
        icone: AlertTriangle, rotulo: 'Atenção',
        cls: 'text-amber-600 dark:text-amber-400',
        caixa: 'border-amber-200 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-900/15',
    },
    bloqueio: {
        icone: ShieldAlert, rotulo: 'Precisa resolver',
        cls: 'text-red-600 dark:text-red-400',
        caixa: 'border-red-200 dark:border-red-800/50 bg-red-50/70 dark:bg-red-900/15',
    },
};

/** Mínimo de texto para a justificativa contar como justificativa, e não como "ok". */
const MIN_JUSTIFICATIVA = 10;

const ValidarCancelamentoModal: React.FC<ValidarCancelamentoModalProps> = ({ isOpen, solicitacao, onClose, onValidado }) => {
    const { addToast } = useToast();
    const { profile } = useAuth();
    // #6239: o cadastro da UC só é corrigido pelo Financeiro ou por adm.
    const podeCorrigirCadastro = podeEditarCadastro(profile);
    const [carregando, setCarregando] = useState(true);
    const [erroCarga, setErroCarga] = useState<string | null>(null);
    const [ctx, setCtx] = useState<ContextoValidacao | null>(null);
    const [form, setForm] = useState<Record<CampoUcEditavel, string>>({} as Record<CampoUcEditavel, string>);
    const [justificativa, setJustificativa] = useState('');
    const [salvando, setSalvando] = useState(false);
    // Carência do plano (cláusula 13.7). Fica em configuração porque a demanda 01
    // respondeu "3 meses" mas o Termo manda olhar o Plano de Benefícios de cada um.
    const [mesesCarencia, setMesesCarencia] = useState<number | null>(null);
    const [percentualMulta, setPercentualMulta] = useState<number | null>(null);
    const [avisoPrevioDias, setAvisoPrevioDias] = useState<number | null>(null);
    const [editando, setEditando] = useState(false);

    useEffect(() => {
        let ativo = true;
        // Parâmetros do Termo que o time pode ajustar sem deploy (#5825).
        supabase.from('system_settings').select('key, value')
            .in('key', ['cancelamento_carencia_meses', 'cancelamento_multa_percentual', 'cancelamento_aviso_previo_dias'])
            .then(({ data }) => {
                if (!ativo) return;
                const numero = (chave: string): number | null => {
                    const n = Number(String((data || []).find(d => d.key === chave)?.value ?? '').trim());
                    return Number.isFinite(n) && n > 0 ? n : null;
                };
                setMesesCarencia(numero('cancelamento_carencia_meses'));
                const pct = numero('cancelamento_multa_percentual');
                // A configuração é em pontos percentuais ("30"); a regra usa fração.
                setPercentualMulta(pct !== null && pct <= 100 ? pct / 100 : null);
                setAvisoPrevioDias(numero('cancelamento_aviso_previo_dias'));
            });
        return () => { ativo = false; };
    }, []);

    useEffect(() => {
        if (!isOpen || !solicitacao) return;
        let cancelado = false;
        setCarregando(true);
        setErroCarga(null);
        setCtx(null);
        setJustificativa(solicitacao.observacao || '');
        setEditando(false);
        carregarContextoValidacao(solicitacao.uc_id, solicitacao.id)
            .then(c => {
                if (cancelado) return;
                setCtx(c);
                const inicial = {} as Record<CampoUcEditavel, string>;
                CAMPOS.forEach(({ campo }) => { inicial[campo] = String((c.uc as any)[campo] ?? ''); });
                setForm(inicial);
            })
            .catch(e => {
                if (cancelado) return;
                console.error('Erro ao montar a validação do cancelamento', e);
                setErroCarga(e?.message || 'Erro desconhecido');
            })
            .finally(() => { if (!cancelado) setCarregando(false); });
        return () => { cancelado = true; };
    }, [isOpen, solicitacao]);

    // Revalida a cada tecla, sem ir ao banco: `validarCancelamento` é pura, então
    // o item sai do vermelho na hora em que o atendente corrige o campo — em vez
    // de só descobrir o resultado depois de salvar.
    const resultado = useMemo(() => {
        if (!ctx) return null;
        return validarCancelamento({ ...ctx, uc: { ...ctx.uc, ...form } });
    }, [ctx, form]);

    const alterados = useMemo(() => {
        if (!ctx) return [] as CampoUcEditavel[];
        return CAMPOS.map(c => c.campo)
            .filter(campo => String((ctx.uc as any)[campo] ?? '') !== String(form[campo] ?? ''));
    }, [ctx, form]);

    // Análise financeira (#5829): só informa, não bloqueia a validação.
    const analise: AnaliseFinanceira | null = useMemo(() => ctx ? montarAnaliseFinanceira({
        faturasEmAbertoQtd: ctx.faturasCobraveisQtd,
        faturasEmAbertoValor: ctx.faturasCobraveisValor,
        saldoAcumuladoKwh: ctx.saldoAcumuladoKwh,
        saldoMesReferencia: ctx.saldoMesReferencia,
        consumoMedioKwh: ctx.consumoMedioKwh,
        valoresBoletosMaisRecentesPrimeiro: ctx.valoresBoletos,
        valoresBoletosPagosMaisRecentesPrimeiro: ctx.valoresBoletosPagos,
        temContrato: !!ctx.contrato,
        fidelidadeTexto: ctx.contrato?.fidelidade ?? null,
        dataAssinaturaTermo: ctx.contrato?.dataAssinaturaTermo ?? null,
        dataPrimeiraCompensacao: ctx.dataPrimeiraCompensacao,
        mesesCarencia: mesesCarencia,
        percentualMulta: percentualMulta,
        avisoPrevioDias: avisoPrevioDias,
        hoje: hojeEmBrasilia(new Date()),
    }) : null, [ctx, mesesCarencia, percentualMulta, avisoPrevioDias]);

    if (!isOpen || !solicitacao) return null;

    const bloqueiosAbertos = resultado ? resultado.bloqueios.length : 0;
    const precisaJustificar = bloqueiosAbertos > 0;
    const justificativaOk = justificativa.trim().length >= MIN_JUSTIFICATIVA;
    const podeSeguir = !!resultado && (!precisaJustificar || justificativaOk);

    const patchDosAlterados = () => {
        const patch: Partial<Record<CampoUcEditavel, string>> = {};
        alterados.forEach(campo => { patch[campo] = form[campo]; });
        return patch;
    };

    const salvarCorrecoes = async () => {
        if (!ctx || alterados.length === 0) return;
        setSalvando(true);
        try {
            const patch = patchDosAlterados();
            await corrigirCadastroUc(ctx.uc.id, patch);
            // O contexto passa a ser o cadastro já corrigido: sem isso `alterados`
            // continuaria acusando diferença e o botão pediria para salvar de novo.
            setCtx({ ...ctx, uc: { ...ctx.uc, ...patch } as ContextoValidacao['uc'] });
            addToast(`${alterados.length} campo(s) corrigido(s) no cadastro da UC.`, 'success');
            setEditando(false);
        } catch (e: any) {
            console.error('Erro ao corrigir cadastro da UC', e);
            addToast('Erro ao salvar as correções: ' + (e.message || ''), 'error');
        } finally {
            setSalvando(false);
        }
    };

    const confirmar = async () => {
        if (!ctx || !resultado) return;
        setSalvando(true);
        try {
            // As correções vão junto: seria pegadinha o atendente arrumar o CPF na
            // tela, seguir para o distrato e o termo sair com o dado velho — o
            // CancelUCModal lê a UC do banco, não este formulário.
            if (alterados.length > 0) await corrigirCadastroUc(ctx.uc.id, patchDosAlterados());
            await registrarValidacao(solicitacao.id, resultado.itens, justificativa, analise);
            addToast('Cancelamento validado. Confira o distrato antes de enviar para assinatura.', 'success');
            onValidado();
        } catch (e: any) {
            console.error('Erro ao registrar a validação do cancelamento', e);
            addToast('Erro ao registrar a validação: ' + (e.message || ''), 'error');
        } finally {
            setSalvando(false);
        }
    };

    /** Severidade que está pendurada num campo, para pintar a borda do input. */
    const problemaDoCampo = (campo: CampoUcEditavel): SeveridadeValidacao | null => {
        const item = resultado?.itens.find(i => i.severidade !== 'ok' && (i.camposParaCorrigir || []).includes(campo));
        return item ? item.severidade : null;
    };

    const bordaDoCampo = (campo: CampoUcEditavel) => {
        const p = problemaDoCampo(campo);
        if (p === 'bloqueio') return 'border-red-300 dark:border-red-700 ring-1 ring-red-200 dark:ring-red-900/50';
        if (p === 'atencao') return 'border-amber-300 dark:border-amber-700';
        return 'border-gray-200 dark:border-slate-700';
    };

    const renderItem = (item: ItemValidacao) => {
        const meta = SEVERIDADE_META[item.severidade];
        const Icone = meta.icone;
        const corrigivel = podeCorrigirCadastro && (item.camposParaCorrigir || []).length > 0;
        return (
            <div key={item.chave} className={`rounded-xl border px-3.5 py-3 flex items-start gap-3 ${meta.caixa}`}>
                <Icone size={16} className={`${meta.cls} shrink-0 mt-0.5`} />
                <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-800 dark:text-slate-100">
                        {item.titulo}
                        <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>{meta.rotulo}</span>
                    </p>
                    <p className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 leading-relaxed">{item.detalhe}</p>
                    {corrigivel && !editando && (
                        <button type="button" onClick={() => setEditando(true)}
                            className="inline-flex items-center gap-1.5 mt-1.5 text-xs font-bold text-primary hover:opacity-80">
                            <PencilLine size={12} /> Corrigir no cadastro
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
            {...propsFundoModal(() => { if (!salvando) onClose(); })}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh]"
                onClick={e => e.stopPropagation()}>

                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-primary/10 text-primary rounded-lg border border-primary/20 shrink-0">
                            <ShieldCheck size={20} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Validar cancelamento</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                                {solicitacao.cliente_nome} — UC {solicitacao.uc_numero} · última conferência antes do distrato
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={salvando} aria-label="Fechar"
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
                    {carregando ? (
                        <div className="py-16 text-center text-gray-400 dark:text-slate-500 text-sm">
                            <Loader2 size={20} className="animate-spin inline mr-2" />
                            Conferindo cadastro, contrato, faturas e cancelamentos anteriores...
                        </div>
                    ) : erroCarga ? (
                        <div className="py-12 text-center">
                            <ShieldAlert size={28} className="text-red-500 mx-auto mb-3" />
                            <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Não foi possível montar a conferência.</p>
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{erroCarga}</p>
                        </div>
                    ) : resultado && (
                        <>
                            <div className={`rounded-2xl border px-4 py-3 flex items-center gap-3 ${resultado.liberado
                                ? 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/70 dark:bg-emerald-900/15'
                                : 'border-red-200 dark:border-red-800/50 bg-red-50/70 dark:bg-red-900/15'}`}>
                                {resultado.liberado
                                    ? <ShieldCheck size={20} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                                    : <ShieldAlert size={20} className="text-red-600 dark:text-red-400 shrink-0" />}
                                <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">
                                    {resultado.liberado
                                        ? (resultado.atencoes.length > 0
                                            ? `Sem bloqueios — ${resultado.atencoes.length} ponto(s) de atenção para ler antes de seguir.`
                                            : 'Conferência sem bloqueios. Pode seguir para o distrato.')
                                        : `${bloqueiosAbertos} ponto(s) precisam ser resolvidos ou justificados antes de o distrato sair.`}
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-gray-800 dark:text-slate-200 border-b border-gray-100 dark:border-slate-700 pb-2 mb-3">
                                    Conferência automática
                                </h3>
                                <div className="flex flex-col gap-2">{resultado.itens.map(renderItem)}</div>
                            </div>

                            {analise && (
                            <div>
                                <h3 className="text-sm font-bold text-gray-800 dark:text-slate-200 border-b border-gray-100 dark:border-slate-700 pb-2 mb-1 flex items-center gap-2">
                                    <Wallet size={15} className="text-gray-400 dark:text-slate-500" aria-hidden="true" /> Análise financeira
                                </h3>
                                <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-2">
                                    Números para informar o cliente na resposta. Não impedem a validação.
                                </p>
                                <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/50 px-3.5 py-2 divide-y divide-gray-200/70 dark:divide-slate-700/70">
                                    <LinhaFinanceira
                                        rotulo="Faturas em aberto"
                                        valor={analise.faturasEmAberto.qtd > 0
                                            ? `${analise.faturasEmAberto.qtd} · ${fmtBRLv(analise.faturasEmAberto.valor)}`
                                            : 'Nenhuma'}
                                        detalhe={analise.faturasEmAberto.qtd > 0 ? 'boletos "Em aberto" ou "Vencido"' : null}
                                    />
                                    <LinhaFinanceira
                                        rotulo="Saldo remanescente"
                                        motivo={analise.saldo.motivo}
                                        valor={analise.saldo.valor && (analise.saldo.valor.temSaldo
                                            ? fmtKwhV(analise.saldo.valor.saldoKwh)
                                            : `Sem saldo (${fmtKwhV(analise.saldo.valor.saldoKwh)})`)}
                                        detalhe={analise.saldo.valor
                                            ? [
                                                analise.saldo.valor.temSaldo && analise.saldo.valor.mesesDeConsumo !== null
                                                    ? (analise.saldo.valor.mesesDeConsumo > 0
                                                        ? `cobre ~${analise.saldo.valor.mesesDeConsumo} mês(es) de consumo`
                                                        : 'cobre menos de 1 mês de consumo')
                                                    : analise.saldo.valor.temSaldo ? 'sem consumo médio para estimar' : null,
                                                analise.saldoMesReferencia ? `fatura de ${analise.saldoMesReferencia}` : null,
                                            ].filter(Boolean).join(' · ') || null
                                            : null}
                                    />
                                    <LinhaFinanceira
                                        rotulo="Base mensal da multa"
                                        motivo={analise.baseMensal.motivo}
                                        valor={analise.baseMensal.valor ? fmtBRLv(analise.baseMensal.valor.valor) : null}
                                        detalhe={analise.baseMensal.valor
                                            ? (analise.baseMensal.valor.origem === 'tres_ultimas_pagas'
                                                ? 'média das 3 últimas contribuições pagas (cláusula 13.4-I)'
                                                : `média de ${plural(analise.baseMensal.valor.consideradas, 'contribuição', 'contribuições')} do período (cláusula 13.4-II)`)
                                            : null}
                                    />
                                    <LinhaFinanceira
                                        rotulo="Carência"
                                        motivo={analise.carencia.motivo}
                                        valor={analise.carencia.valor && (analise.carencia.valor.dentroDaCarencia
                                            ? `Dentro da carência (${analise.carencia.valor.mesesTotal} meses)`
                                            : 'Carência encerrada')}
                                        detalhe={analise.carencia.valor
                                            ? (analise.carencia.valor.dentroDaCarencia
                                                ? `até ${fmtDataBr(analise.carencia.valor.fimDaCarencia)} · sai sem multa (cláusula 13.7)`
                                                : `encerrou em ${fmtDataBr(analise.carencia.valor.fimDaCarencia)}`)
                                            : null}
                                    />
                                    <LinhaFinanceira
                                        rotulo="Fidelidade"
                                        motivo={analise.fidelidade.motivo}
                                        valor={analise.fidelidade.valor && (analise.fidelidade.valor.mesesTotal === 0
                                            ? 'Contrato sem fidelidade'
                                            : analise.fidelidade.valor.dentroDaFidelidade
                                                ? `Faltam ${analise.fidelidade.valor.mesesRestantes} de ${analise.fidelidade.valor.mesesTotal} meses`
                                                : 'Fidelidade já cumprida')}
                                        detalhe={analise.fidelidade.valor && analise.fidelidade.valor.mesesTotal > 0
                                            ? `termina em ${fmtDataBr(analise.fidelidade.valor.fimDaFidelidade)}`
                                                + (analise.inicioContagem.data
                                                    ? ` · conta de ${fmtDataBr(analise.inicioContagem.data)} (${analise.inicioContagem.origem === 'primeira_compensacao'
                                                        ? '1ª compensação, cláusula 13.8' : 'assinatura do termo — sem fatura com crédito ainda'})`
                                                    : '')
                                            : null}
                                    />
                                    <LinhaFinanceira
                                        rotulo="Multa"
                                        motivo={analise.multa.motivo}
                                        valor={analise.multa.valor ? fmtBRLv(analise.multa.valor.valor) : null}
                                        detalhe={analise.multa.valor ? analise.multa.valor.demonstrativo : null}
                                    />
                                    <LinhaFinanceira
                                        rotulo="Aviso prévio"
                                        valor={`${analise.avisoPrevioDias} dias`}
                                        detalhe="prazo mínimo de notificação da saída (Quadro Resumo do Termo)"
                                    />
                                </div>
                            </div>
                            )}

                            <DocumentosCancelamento
                                solicitacaoId={solicitacao.id}
                                solicitanteNome={solicitacao.solicitanteNome}
                                solicitanteVinculo={solicitacao.solicitanteVinculo}
                                solicitanteContato={[solicitacao.solicitanteEmail, solicitacao.solicitanteTelefone].filter(Boolean).join(' · ') || null}
                            />

                            <div>
                                <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 pb-2 mb-1 gap-2">
                                    <h3 className="text-sm font-bold text-gray-800 dark:text-slate-200">Dados que vão para o distrato</h3>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {alterados.length > 0 && (
                                            <button type="button" onClick={salvarCorrecoes} disabled={salvando}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-primary text-xs font-bold hover:bg-primary/20 transition-colors disabled:opacity-50">
                                                <Save size={12} /> Salvar {alterados.length} correção(ões)
                                            </button>
                                        )}
                                        {podeCorrigirCadastro ? (
                                            <button type="button" onClick={() => setEditando(v => !v)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 text-xs font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                                                <PencilLine size={12} /> {editando ? 'Só conferir' : 'Editar'}
                                            </button>
                                        ) : (
                                            <span className="text-[11px] text-gray-400 dark:text-slate-500">Correção: só o Financeiro</span>
                                        )}
                                    </div>
                                </div>
                                <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-3">
                                    Sai do cadastro da UC. O que for corrigido aqui é gravado no cadastro, não só neste distrato.
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                                    {CAMPOS.map(({ campo, rotulo, largura, placeholder }) => (
                                        <div key={campo} className={`col-span-2 ${largura}`}>
                                            <label className={labelCls} htmlFor={`validacao-${campo}`}>{rotulo}</label>
                                            {editando ? (
                                                <input
                                                    id={`validacao-${campo}`}
                                                    value={form[campo] ?? ''}
                                                    placeholder={placeholder}
                                                    onChange={e => setForm(f => ({ ...f, [campo]: e.target.value }))}
                                                    className={`${inputCls} ${bordaDoCampo(campo)}`}
                                                />
                                            ) : (
                                                <p className={`text-sm px-3 py-2 rounded-xl border truncate bg-gray-50/60 dark:bg-slate-800/50 ${bordaDoCampo(campo)} ${form[campo]
                                                    ? 'text-gray-800 dark:text-slate-100' : 'text-gray-400 dark:text-slate-500 italic'}`}
                                                    title={form[campo] || undefined}>
                                                    {form[campo] || 'não informado'}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className={labelCls} htmlFor="validacao-justificativa">
                                    Situações especiais e observações{' '}
                                    {precisaJustificar
                                        ? <span className="text-red-600 dark:text-red-400">* obrigatória — há bloqueio em aberto</span>
                                        : <span className="normal-case font-semibold text-gray-400 dark:text-slate-500">(opcional)</span>}
                                </label>
                                <textarea
                                    id="validacao-justificativa"
                                    value={justificativa}
                                    onChange={e => setJustificativa(e.target.value)}
                                    rows={3}
                                    placeholder={precisaJustificar
                                        ? 'Explique por que o cancelamento segue mesmo com o(s) ponto(s) em vermelho...'
                                        : 'Combinados com o cliente, acordo de multa, prazos prometidos...'}
                                    className={`${inputCls} resize-y ${precisaJustificar && !justificativaOk
                                        ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-slate-700'}`}
                                />
                                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">
                                    Fica gravado com o seu nome no histórico da solicitação, junto com o quadro acima do jeito que ele está agora.
                                </p>
                            </div>
                        </>
                    )}
                </div>

                <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="text-[11px] text-gray-500 dark:text-slate-400">
                        {precisaJustificar
                            ? 'Corrija os pontos em vermelho ou escreva a justificativa para liberar o distrato.'
                            : 'O distrato só é gerado na próxima tela — nada é enviado ao cliente ainda.'}
                    </p>
                    <div className="flex justify-end gap-3 shrink-0">
                        <button type="button" onClick={onClose} disabled={salvando}
                            className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50">
                            Fechar
                        </button>
                        <button type="button" onClick={confirmar} disabled={!podeSeguir || salvando || carregando}
                            className="px-5 py-2 bg-primary hover:opacity-90 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 min-w-[210px]">
                            {salvando ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                            {salvando ? 'Registrando...' : 'Validar e gerar distrato'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ValidarCancelamentoModal;
